import type {
  LessonCallDetail,
  TranscriptFragment,
} from "@repo/app-types/client/client";
import { TranscriptFragmentRole } from "@repo/app-types/entities/transcriptFragment";
import { assertUnreachable } from "@repo/app-types/helpers/utilities";
import { TextSkeleton } from "@repo/ui/Skeletons/skeletons";
import { IconPhone } from "@tabler/icons-react";
import { DateTime } from "luxon";
import { type FC, useMemo } from "react";
import { twMerge } from "tailwind-merge";
import { AUDIO_URL_REFRESH_INTERVAL_MS } from "../../api/entities/recording.js";
import {
  useTrainingParticipantLessonCallsDetailsQuery,
  useTrainingParticipantLessonTranscriptQuery,
} from "../../api/entities/trainingParticipant.js";
import type { AudioPlayerSource } from "../../components/audioPlayer/context.js";
import { useAudioPlayer } from "../../components/audioPlayer/hook.js";
import { AudioPlayerPlayButton } from "../../components/audioPlayer/playButton.js";
import { AudioPlayerTimestampPlayButton } from "../../components/audioPlayer/timestampPlayButton.js";
import { TranscriptFragmentText } from "../insights/transcriptFragmentText.js";

export interface LessonTranscriptProps {
  projectId: string;
  title: string;
  participantId: string;
  interviewSectionDefinitionId: string;
  /** Specific completed attempt to view. When omitted, the server returns the most recently completed attempt. */
  interviewSectionResultId?: string;
}

export const LessonTranscript: FC<LessonTranscriptProps> = (props) => {
  const {
    currentData: transcript,
    isFetching: isLoadingTranscripts,
    isError: isErrorTranscripts,
  } = useTrainingParticipantLessonTranscriptQuery({
    projectId: props.projectId,
    participantId: props.participantId,
    interviewSectionDefinitionId: props.interviewSectionDefinitionId,
    interviewSectionResultId: props.interviewSectionResultId,
  });

  const { data: callsDetails } = useTrainingParticipantLessonCallsDetailsQuery(
    {
      projectId: props.projectId,
      participantId: props.participantId,
      interviewSectionDefinitionId: props.interviewSectionDefinitionId,
      interviewSectionResultId: props.interviewSectionResultId,
    },
    // Recording URLs are presigned with a limited expiry
    { pollingInterval: AUDIO_URL_REFRESH_INTERVAL_MS },
  );

  const audioPlayerSources: readonly AudioPlayerSource[] | undefined = useMemo(
    () =>
      callsDetails?.map((call, callIndex, { length: callCount }) => ({
        key: call.id,
        title: props.title,
        subtitle: `Call ${callIndex + 1} of ${callCount}`,
        sortTimestamp: DateTime.fromISO(call.initiated_at).toMillis(),
        src: call.recording_url,
      })),
    [props.title, callsDetails],
  );

  useAudioPlayer({
    sources: audioPlayerSources,
  });

  const isVoiceTranscriptFragments =
    transcript && "voice_transcript_fragments" in transcript;

  const voiceTimeline = isVoiceTranscriptFragments
    ? buildVoiceTimeline(
        transcript.voice_transcript_fragments,
        callsDetails ?? [],
      )
    : undefined;

  const renderVoiceTimelineItem = (
    item: VoiceTimelineItem,
    index: number,
    timeline: VoiceTimelineItem[],
  ) => {
    switch (item.type) {
      case "fragment":
        return (
          <div
            key={item.fragment.id}
            className="flex items-start justify-between gap-2 text-sm text-foreground"
          >
            <TranscriptFragmentText
              className="my-0"
              text={item.fragment.text_transcript}
              role={item.fragment.role}
            />
            {item.callId !== null && item.relativeTime !== null ? (
              <div
                className={twMerge(
                  item.fragment.role === TranscriptFragmentRole.AGENT
                    ? "my-0"
                    : "my-1",
                  "flex h-5 items-center",
                )}
              >
                <AudioPlayerTimestampPlayButton
                  sourceKey={item.callId}
                  time={item.relativeTime}
                  nextTime={findNextTimeOffsetSeconds(
                    timeline,
                    index,
                    item.callId,
                  )}
                />
              </div>
            ) : null}
          </div>
        );
      case "call":
        return (
          <div key={item.call.id} className="flex items-center space-x-2">
            <IconPhone className="size-4 text-green-500" />
            <span>Call started</span>
            <span className="text-sm text-muted-foreground">
              {DateTime.fromISO(item.call.initiated_at).toLocaleString(
                DateTime.DATETIME_FULL_WITH_SECONDS,
              )}
            </span>
            <AudioPlayerPlayButton sourceKey={item.call.id} />
          </div>
        );
      default:
        assertUnreachable(item);
    }
  };

  return (
    <div>
      {!transcript && isLoadingTranscripts ? <TextSkeleton /> : null}

      {!transcript && isErrorTranscripts ? (
        <div className="text-sm">This transcript is not yet available.</div>
      ) : null}

      {isVoiceTranscriptFragments && voiceTimeline ? (
        <div className="space-y-4">
          {voiceTimeline.map(renderVoiceTimelineItem)}
        </div>
      ) : null}

      {transcript && "chat_messages" in transcript ? (
        <div className="space-y-4">
          {transcript.chat_messages.map((message) => (
            <div key={message.id} className="text-sm text-foreground">
              <TranscriptFragmentText
                text={message.content}
                role={
                  message.role === "user"
                    ? TranscriptFragmentRole.PARTICIPANT
                    : TranscriptFragmentRole.AGENT
                }
              />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

type VoiceTimelineItem = VoiceTimelineFragmentItem | VoiceTimelineCallItem;

interface VoiceTimelineFragmentItem {
  type: "fragment";
  time: DateTime;
  fragment: TranscriptFragment;
  /** The call ID this fragment belongs to, used as the audio player source key. */
  callId: string | null;
  /** The offset in seconds from the start of the call. */
  relativeTime: number | null;
}

interface VoiceTimelineCallItem {
  type: "call";
  time: DateTime;
  call: LessonCallDetail;
}

function buildVoiceTimeline(
  fragments: readonly TranscriptFragment[],
  callsDetails: readonly LessonCallDetail[],
): VoiceTimelineItem[] {
  const voiceTimelineCalls = callsDetails
    .map(
      (call): VoiceTimelineCallItem => ({
        type: "call",
        time: DateTime.fromISO(call.initiated_at),
        call,
      }),
    )
    .toSorted((a, b) => a.time.valueOf() - b.time.valueOf());

  const voiceTimelineFragments = fragments.map(
    (fragment): VoiceTimelineFragmentItem => {
      const time = DateTime.fromISO(fragment.start_time);

      const callItem = voiceTimelineCalls.findLast((c) => c.time <= time);
      const relativeTime = callItem
        ? time.diff(callItem.time, "seconds").as("seconds")
        : null;

      return {
        type: "fragment",
        time,
        fragment,
        callId: callItem?.call.id ?? null,
        relativeTime,
      };
    },
  );

  return [...voiceTimelineCalls, ...voiceTimelineFragments].toSorted(
    (a, b) => a.time.valueOf() - b.time.valueOf(),
  );
}

function findNextTimeOffsetSeconds(
  timeline: readonly VoiceTimelineItem[],
  index: number,
  callId: string,
): number | undefined {
  for (let i = index + 1; i < timeline.length; i++) {
    const next = timeline[i];
    if (!next) {
      continue;
    }

    if (next.type === "fragment" && next.callId === callId) {
      return next.relativeTime ?? undefined;
    }

    // If we hit a different call, stop looking
    if (next.type === "call") {
      break;
    }
  }
  return undefined;
}
